import React, { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useState } from 'react';
import Logo from '../../assets/Logo.png';
import './styles.scss';
import { Image } from 'react-bootstrap';
import { InvoicePreview } from '../../models/dashboard-models';
import { toast } from "react-toastify";
import { CustomToastContent } from "../CustomToastContent";
import { toastOptionsTopRight } from "../../utils/Enums/ToastOptions";

interface EditableInvoiceProps {
    invoicePreviewData: InvoicePreview;
    setHasChanges: Dispatch<SetStateAction<boolean>>;
}

const InvoiceForm = forwardRef<any, EditableInvoiceProps>(({ invoicePreviewData, setHasChanges }, ref) => {
    const [id, setId] = useState(invoicePreviewData.id);
    const [invoiceNumber, setInvoiceNumber] = useState(invoicePreviewData.invoiceNumber);
    const [date, setDate] = useState(invoicePreviewData.date);
    const [invoiceCreditNumber, setInvoiceCreditNumber] = useState(invoicePreviewData.invoiceCreditNumber);
    const [creditDate, setCreditDate] = useState(invoicePreviewData.creditDate);
    const [duePaymentDate, setDuePaymentDate] = useState(invoicePreviewData.duePaymentDate);

    const {
        clientCompanyName,
        clientTown,
        clientAddress,
        clientOwner,
        clientEik,
        clientVatPrefix,
        clientVat,
    } = invoicePreviewData;

    const [aromaCompanyName, setAromaCompanyName] = useState(invoicePreviewData.aromaCompanyName);
    const [aromaCity, setAromaCity] = useState(invoicePreviewData.aromaTown);
    const [aromaAddress, setAromaAddress] = useState(invoicePreviewData.aromaAddress);
    const [aromaOwner, setAromaOwner] = useState(invoicePreviewData.aromaOwner);
    const [aromaEik, setAromaEik] = useState(invoicePreviewData.aromaEik);
    const [aromaVatPrefix, setAromaVatPrefix] = useState(invoicePreviewData.aromaVatPrefix);
    const [aromaVat, setAromaVat] = useState(invoicePreviewData.aromaVat);
    const [bankName, setBankName] = useState(invoicePreviewData.bankName);
    const [iban, setIban] = useState(invoicePreviewData.iban);
    const [swift, setSwift] = useState(invoicePreviewData.swift);

    const [text, setText] = useState(invoicePreviewData.text);
    const [fromDate, setFromDate] = useState(invoicePreviewData.fromDate);
    const [dueToDate, setDueToDate] = useState(invoicePreviewData.dueToDate);
    const [months, setMonths] = useState(parseFloat(invoicePreviewData.months).toFixed(2));
    const [currency, setCurrency] = useState(invoicePreviewData.currency);
    const [currencySymbol, setCurrencySymbol] = useState(invoicePreviewData.currencySymbol || '€');
    const [price, setPrice] = useState((parseFloat(invoicePreviewData.price) / 100).toFixed(2));
    const [totalPriceForInvoice, setTotalPriceForInvoice] = useState((parseFloat(invoicePreviewData.totalPriceForInvoice) / 100).toFixed(2));

    const [total, setTotal] = useState(invoicePreviewData.total);
    const [discount, setDiscount] = useState(invoicePreviewData.discount);
    const [discounted, setDiscounted] = useState(invoicePreviewData.discounted);
    const [totalWithDiscount, setTotalWithDiscount] = useState(invoicePreviewData.totalWithDiscount);
    const [dds, setDds] = useState(invoicePreviewData.dds);
    const [ddsPrice, setDdsPrice] = useState(invoicePreviewData.ddsPrice);
    const [totalPrice, setTotalPrice] = useState(invoicePreviewData.totalPrice);

    const [spelledPrice, setSpelledPrice] = useState(invoicePreviewData.spelledPrice);
    const [paid, setPaid] = useState(invoicePreviewData.paid);

    const [receiver, setReceiver] = useState(invoicePreviewData.receiver);
    const [userFullName, setUserFullName] = useState(invoicePreviewData.userFullName);
    const [items, setItems] = useState(invoicePreviewData.items);
    const isInvoice = invoicePreviewData?.documentType === "INVOICE";

    // Handle input changes
    const handleInputChange = (setterFunction: React.Dispatch<React.SetStateAction<string>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (invoiceNumber !== " ") {
            toast.error(<CustomToastContent
                content={['Не може да редактирате фактура с номер']} />, toastOptionsTopRight);
        } else {
            setterFunction(event.target.value);
            setHasChanges(true);
        }
    };

    const getInvoiceData = () => {
        return {
            id,
            invoiceNumber,
            date,
            clientCompanyName,
            clientTown,
            clientAddress,
            clientOwner,
            clientEik,
            clientVatPrefix,
            clientVat,
            aromaCompanyName,
            aromaCity,
            aromaAddress,
            aromaOwner,
            aromaEik,
            aromaVatPrefix,
            aromaVat,
            bankName,
            iban,
            swift,
            text,
            fromDate,
            dueToDate,
            months,
            currency,
            price,
            totalPriceForInvoice,
            total,
            discount,
            discounted,
            totalWithDiscount,
            dds,
            ddsPrice,
            totalPrice,
            spelledPrice,
            paid,
            receiver,
            userFullName,
            items
        };
    };

    useEffect(() => {
        setInvoiceNumber(invoicePreviewData.invoiceNumber);
        setInvoiceCreditNumber(invoicePreviewData.invoiceCreditNumber);
        setCreditDate(invoicePreviewData.creditDate);
        setDate(invoicePreviewData.date);
        setItems(invoicePreviewData.items);
        setDuePaymentDate(invoicePreviewData.duePaymentDate);
        setPrice(invoicePreviewData.price);
        setTotal(invoicePreviewData.total);
        setDiscount(invoicePreviewData.discount);
        setDiscounted(invoicePreviewData.discounted);
        setTotalWithDiscount(invoicePreviewData.totalWithDiscount);
        setDds(invoicePreviewData.dds);
        setDdsPrice(invoicePreviewData.ddsPrice);
        setTotalPrice(invoicePreviewData.totalPrice);
        setSpelledPrice(invoicePreviewData.spelledPrice);
        setPaid(invoicePreviewData.paid);
    }, [invoicePreviewData]);

    useImperativeHandle(ref, () => ({
        getInvoiceData,
    }));

    return (
        <div style={{
            width: '200mm',
            height: '100%',
        }}>
            <div style={{ display: 'flex' }}>
                <div style={{ flex: 1 }}>
                    <Image className='w-75 pb-4' src={Logo}></Image>
                </div>
                <div style={{ flex: 1 }}>
                    <h2 style={{ textAlign: 'center', fontSize: 24 }}>
                        <br />
                        {id}
                        <br />
                        № {invoiceCreditNumber != " " ? invoiceCreditNumber : invoiceNumber}/Дата: {invoiceCreditNumber != " " ? creditDate : date}г.
                        <br />
                        ОРИГИНАЛ
                    </h2>
                    {invoiceCreditNumber != " " && <div>
                        <br />
                        <br />
                        <div>Към {isInvoice ? 'фактура' : 'стокова разписка'}: {invoiceNumber} / {date} г.</div>
                        <div>Причина сторно: Операторска грешка</div>
                    </div>}
                </div>
            </div>
            <div style={{ display: 'flex', margin: 15 }}>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0 }}>
                        <b>Получател:</b>
                    </p>
                    <div className='data'>
                        <div>
                            Име: {clientCompanyName}
                        </div>
                        <div>
                            Град: {clientTown}
                        </div>
                        <div>
                            Адрес: {clientAddress}
                        </div>
                        <div>
                            ЕИК: {clientEik}
                        </div>
                        <div>
                            ДДС номер: {clientVatPrefix}{clientVat}
                        </div>
                        <div>
                            Обект: {invoicePreviewData.branchName}
                        </div>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0 }}>
                        <b>Доставчик:</b>
                    </p>
                    <div className='data' style={{ marginBottom: '50px' }}>
                        <div>
                            Име: {aromaCompanyName}
                        </div>
                        <div>
                            Град: {aromaCity}
                        </div>
                        <div>
                            Адрес: {aromaAddress}
                        </div>
                        <div>
                            МОЛ: {aromaOwner}
                        </div>
                        <div>
                            ЕИК: {aromaEik}
                        </div>
                        <div>
                            ДДС номер: {aromaVatPrefix}{aromaVat}
                        </div>
                        <div>
                            {bankName}
                        </div>
                        <div>
                            IBAN: {iban}
                        </div>
                        <div>
                            Swift: {swift}
                        </div>
                        {invoicePreviewData.distributor && (
                            <div>
                                Дистрибутор: {invoicePreviewData.distributor}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <table className='bordered'>
                <thead>
                    <tr>
                        <td>№</td>
                        <td>Наименование на стоката</td>
                        <td>Кол-во</td>
                        <td>Мярка</td>
                        <td>Ед.цена</td>
                        <td>Общо в {currency === 'BGN' ? 'ЛВ' : 'ЕВРО'}</td>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={index}>
                            <td>
                                {index + 1}
                            </td>
                            <td>
                                {item.text}
                            </td>
                            <td>
                                {item.mounts}
                            </td>
                            <td>
                                брой
                            </td>
                            <td>
                                {((invoiceCreditNumber !== ' ' ? -1 : 1) * item.price / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: false })}{currencySymbol}
                            </td>
                            <td>
                                {((invoiceCreditNumber !== ' ' ? -1 : 1) * Math.round(item.total) / 100).toFixed(2)}{currencySymbol}
                            </td>
                        </tr>
                    ))}
                    {/*<tr>*/}
                    {/*    <td>*/}
                    {/*        1*/}
                    {/*    </td>*/}
                    {/*    <td>*/}
                    {/*        <input*/}
                    {/*            type='text'*/}
                    {/*            value={text}*/}
                    {/*            className={'bordered'}*/}
                    {/*            style={{width: `${text.length}ch`}}*/}
                    {/*            onChange={handleInputChange(setText)}*/}
                    {/*        />*/}
                    {/*        <input*/}
                    {/*            type='text'*/}
                    {/*            className={'bordered'}*/}
                    {/*            value={fromDate}*/}
                    {/*            style={{width: `${fromDate.length}ch`}}*/}
                    {/*            onChange={handleInputChange(setFromDate)}*/}
                    {/*        />*/}
                    {/*        -*/}
                    {/*        <input*/}
                    {/*            type='text'*/}
                    {/*            value={dueToDate}*/}
                    {/*            className={'bordered'}*/}
                    {/*            style={{width: `${dueToDate.length}ch`}}*/}
                    {/*            onChange={handleInputChange(setDueToDate)}*/}
                    {/*        />*/}
                    {/*    </td>*/}
                    {/*    <td>*/}
                    {/*        <input*/}
                    {/*            type='text'*/}
                    {/*            value={months}*/}
                    {/*            className={'bordered'}*/}
                    {/*            style={{width: `${months.length + 1}ch`}}*/}
                    {/*            onChange={handleInputChange(setMonths)}*/}
                    {/*        />*/}
                    {/*    </td>*/}
                    {/*    <td>*/}
                    {/*        {currency}*/}
                    {/*    </td>*/}
                    {/*    <td>*/}
                    {/*        {price}*/}
                    {/*    </td>*/}
                    {/*    <td>*/}
                    {/*        {totalPriceForInvoice}*/}
                    {/*    </td>*/}
                    {/*</tr>*/}
                </tbody>
            </table>

            {/* Other Sections */}
            <div style={{ display: 'flex' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ margin: '20px 0' }}>
                        Стойност на {isInvoice ? 'фактурата' : 'стоковата разписка'}  словом:{' '}
                        {spelledPrice}
                    </div>
                    {duePaymentDate && <div style={{ margin: '20px 0' }}>
                        <b>
                            Срок за плащане без удръжки: {duePaymentDate}
                        </b>
                    </div>}
                    <div style={{ margin: '20px 0' }}>
                        <b>
                            Начин на плащане: {paid}
                        </b>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <table style={{ float: 'right', marginTop: 20 }}>
                        <tbody style={{ textAlign: 'right' }}>
                            <tr>
                                <td style={{ paddingRight: 10, textAlign: 'left', paddingBottom: 5 }}>Общо:</td>
                                <td>
                                    {(invoiceCreditNumber != " " ? "-" : "") + total}{currencySymbol}
                                </td>
                            </tr>
                            {invoiceCreditNumber == " " &&
                                <tr>
                                    <td style={{ paddingRight: 10, textAlign: 'left', paddingBottom: 5 }}>
                                        Отстъпка ({discount}%):
                                    </td>
                                    <td>
                                        {discounted}{currencySymbol}
                                    </td>
                                </tr>}
                            <tr>
                                <td style={{ textAlign: 'left', paddingBottom: 5, paddingRight: 10 }}>Облагаема сума:</td>
                                <td>
                                    {(invoiceCreditNumber != " " ? "-" : "") + totalWithDiscount}{currencySymbol}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ paddingRight: 10, textAlign: 'left', paddingBottom: 5 }}>Данъчна ставка ДДС:
                                </td>
                                <td>
                                    {dds}%
                                </td>
                            </tr>
                            <tr>
                                <td style={{ paddingRight: 10, textAlign: 'left', paddingBottom: 5 }}>Стойност на ДДС:</td>
                                <td>
                                    {(invoiceCreditNumber != " " ? "-" : "") + ddsPrice}{currencySymbol}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ paddingRight: 10, textAlign: 'left', paddingBottom: 5 }}>Сума за плащане:</td>
                                <td>
                                    {/*//<input*/}
                                    {/*  //  type='text'*/}
                                    {/*    value={totalPrice}*/}
                                    {/*    //style={{width: `${totalPrice.length}ch`}}*/}
                                    {/*    //className={'bordered'}*/}
                                    {/*    //onChange={handleInputChange(setTotalPrice)}*/}
                                    {/*/>*/}
                                    {(invoiceCreditNumber != " " ? "-" : "") + totalPrice}{currencySymbol}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer Section */}
            <div style={{ textAlign: 'right', width: '99%', marginTop: '50px' }}>
                <div style={{ display: 'flex', textAlign: 'center' }}>
                    <div style={{ flex: 1 }}>
                        Получател: {receiver}
                        <br />
                        Подпис/........................./
                    </div>
                    <div style={{ flex: 1 }}>
                        Съставител: {userFullName}
                        <br />
                        (ПЕЧАТ) Подпис/........................./
                    </div>
                </div>
            </div>
        </div>
    );
});

export default InvoiceForm;
